import React from "react";
import type {
  AnalyticsContext,
  AnalyticsSettings,
  AnalyticsSettingsUpdate,
} from "./types";
import { initAnalytics } from "./ga";
import * as GA from "./ga";
import { initDefaultConsent, isConsentGranted, subscribeToConsentChanges } from "./consent";
import { initGlobalClickTracker } from "./globalClickTracker";

/**
 * React integration helpers that expose analytics settings, context, and
 * tracking functions through a context provider.
 */

type AnalyticsContextValue = {
  context: AnalyticsContext;
  setContext: (ctx: AnalyticsContext) => void;
  settings: AnalyticsSettings;
  configure: (update: AnalyticsSettingsUpdate | AnalyticsSettings) => void;
  track: typeof GA;
};

const Ctx = React.createContext<AnalyticsContextValue | null>(null);

/**
 * Provides analytics state and helpers to descendant components.
 *
 * The provider ensures consent is initialized, lazily loads the analytics SDK
 * when enabled, and exposes the underlying tracking helpers supplied by the
 * GA module. Consumers can access the context via {@link useAnalytics}.
 */
export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const [ctx, setCtx] = React.useState<AnalyticsContext>({});
  const [settings, setSettings] = React.useState<AnalyticsSettings>(() => GA.getSettings());
  const [hasConsent, setHasConsent] = React.useState(() => isConsentGranted());

  React.useEffect(() => {
    // Initialize lazy analytics and default deny consent
    initDefaultConsent();
    // Initialize global click tracker for data-track-* attribute tracking
    initGlobalClickTracker();
  }, []);

  React.useEffect(() => {
    const unsubscribe = subscribeToConsentChanges(setHasConsent);
    return unsubscribe;
  }, []);

  React.useEffect(() => {
    if (settings.enabled && (!settings.respectConsent || hasConsent)) {
      void initAnalytics();
    }
  }, [settings.enabled, settings.respectConsent, hasConsent]);

  const setContext = React.useCallback((partial: AnalyticsContext) => {
    setCtx((prev) => {
      const next = { ...prev, ...partial };
      GA.setContext(next);
      return next;
    });
  }, []);

  const configure = React.useCallback(
    (update: AnalyticsSettingsUpdate | AnalyticsSettings) => {
      GA.setSettings(update);
      setSettings(GA.getSettings());
    },
    []
  );

  const value: AnalyticsContextValue = React.useMemo(() => ({
    context: ctx,
    setContext,
    settings,
    configure,
    track: GA,
  }), [ctx, settings, setContext, configure]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Hook that exposes the analytics context provided by {@link AnalyticsProvider}.
 *
 * @throws Error when used outside of an `AnalyticsProvider`.
 */
export function useAnalytics() {
  const v = React.useContext(Ctx);
  if (!v) throw new Error("useAnalytics must be used within AnalyticsProvider");
  return v;
}
